import { useCallback, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { Cartridge } from "../models/Cartridge";
import { CARTRIDGE_INITIAL_VALUES } from "../models/Cartridge";
import { useCartridges, useMillSizes } from "../hooks/useCartridges";
import {
	useUpsertCartridge,
	useRemoveCartridge,
} from "../hooks/useCartridgeMutation";
import { CartridgesTable } from "./CartridgesTable";
import { CartridgeDetailViewer } from "./CartridgeDetailViewer";
import { CartridgesToolbar } from "./CartridgesToolbar";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import {
	urlParam,
	useDropUnknownValue,
	useUrlState,
	useUrlText,
} from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

// Default layout percentages (left: 55%, right: 45%)
const DEFAULT_LAYOUT = { left: 65, right: 35 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	millSizeId: urlParam.int(),
	cartridgeId: urlParam.int(),
};

export const CartridgesManager = () => {
	// Filters and the selected record, all in the query string.
	const [
		{ search, onlyActives, millSizeId, cartridgeId },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	// A link to a mill size that is no longer offered falls back to all of them.
	const { data: millSizeOptions = [], isSuccess: millSizeOptionsLoaded } =
		useMillSizes();
	useDropUnknownValue(
		millSizeId,
		millSizeOptionsLoaded,
		millSizeOptions.map((option) => option.Id),
		useCallback(() => dropValues("millSizeId"), [dropValues]),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [cartridgeToDelete, setCartridgeToDelete] = useState<Cartridge | null>(
		null,
	);

	// Resizable panels layout persistence
	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "cartridges-manager-v2",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	// Data fetching
	const {
		data: cartridges = [],
		isLoading,
		error: queryError,
	} = useCartridges({ millSizeId });

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<Cartridge>({
		id: cartridgeId,
		setId: (value) => setFilters({ cartridgeId: value }),
		dropId: () => dropValues("cartridgeId"),
		rows: cartridges,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedCartridge = selection.current;

	// Mutations
	const pendingSaveRef = useRef<{ isNew: boolean; selectRow: boolean }>({
		isNew: false,
		selectRow: true,
	});

	const { upsertCartridge, isUpserting } = useUpsertCartridge({
		onSuccess: (data) => {
			const { isNew, selectRow } = pendingSaveRef.current;
			setNotification(
				isNew
					? "Cartridge created successfully"
					: "Cartridge updated successfully",
			);
			if (data && selectRow) {
				selection.adopt(data);
			}
		},
		onError: (err) => setError(err.message),
	});

	const { removeCartridge, isRemoving } = useRemoveCartridge({
		onSuccess: () => {
			setNotification("Cartridge deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setCartridgeToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	// Handlers
	const handleRowSelected = useCallback(
		(cartridge: Cartridge | null) => {
			if (cartridge) selection.select(cartridge);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((cartridge: Cartridge) => {
		setCartridgeToDelete(cartridge);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (cartridgeToDelete?.Id) {
			removeCartridge(cartridgeToDelete.Id);
		}
	}, [cartridgeToDelete, removeCartridge]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setCartridgeToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		selection.startNew({ ...CARTRIDGE_INITIAL_VALUES });
	}, [selection]);

	const handleSave = useCallback(
		(cartridge: Cartridge) => {
			pendingSaveRef.current = { isNew: !cartridge.Id, selectRow: true };
			upsertCartridge(cartridge);
		},
		[upsertCartridge],
	);

	const handleClose = useCallback(() => {
		selection.clear();
	}, [selection]);

	// Filtered client-side: the detail panel validates duplicate names against the
	// full list, so the query must keep returning inactive cartridges too.
	const filteredCartridges = cartridges.filter(
		(c) =>
			(!onlyActives || c.Active) &&
			(!searchText ||
				c.Name.toLowerCase().includes(searchText.toLowerCase()) ||
				(c.Description &&
					c.Description.toLowerCase().includes(searchText.toLowerCase()))),
	);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Cartridges
			</Typography>

			<Paper elevation={3}>
				<CartridgesToolbar
					onReset={() => resetValues(["search", "onlyActives", "millSizeId"])}
					canReset={!isDefault("search", "onlyActives", "millSizeId")}
					search={searchText}
					onSearchChange={setSearchText}
					millSizeId={millSizeId}
					onMillSizeChange={(value) => setFilters({ millSizeId: value })}
					onlyActives={onlyActives}
					onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
					onAdd={handleAdd}
				/>
			</Paper>

			<Group
				id="cartridges-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={3}
							sx={{
								flex: 1,
								width: "100%",
								overflow: "hidden",
							}}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<CartridgesTable
									cartridges={filteredCartridges}
									selectedCartridge={
										selectedCartridge?.Id ? selectedCartridge : null
									}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 48,
							borderRadius: 1,
							backgroundColor: "#e0e0e0",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: "3px",
							transition: "background-color 0.2s",
							"&:hover": {
								backgroundColor: "primary.main",
								"& .grip-dot": {
									backgroundColor: "#fff",
								},
							},
						}}
					>
						{[...Array(5)].map((_, i) => (
							<Box
								key={`grip-dot-${i}`}
								className="grip-dot"
								sx={{
									width: 4,
									height: 4,
									borderRadius: "50%",
									backgroundColor: "#9e9e9e",
									transition: "background-color 0.2s",
								}}
							/>
						))}
					</Box>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								overflowY: "auto",
								overflowX: "hidden",
							}}
						>
							<CartridgeDetailViewer
								cartridge={selectedCartridge}
								existingCartridges={cartridges}
								isSaving={isUpserting || isRemoving}
								onSave={handleSave}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE CARTRIDGE"
				itemName={cartridgeToDelete?.Name ?? ""}
				isDeleting={isRemoving}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
